import type { OpLog } from '@panels/shared';
import { submitPanel } from './api.js';
import { ApiError } from './api.js';
import type { DrawingSnapshot } from './drawing.js';
import { classifySubmitFailure } from './submitErrors.js';

/**
 * GUARDRAIL #8: the in-progress drawing survives a refresh, a back-swipe, or the
 * browser being killed. Drafts are keyed by panel assignment and include the redo
 * stack, so an interrupted session comes back exactly as it was.
 *
 * GUARDRAIL #7: submission is bulletproof. A finished panel is written to a local
 * outbox *before* the network is touched, retried with backoff, and only cleared
 * once the server has confirmed it. Losing someone's drawing is the worst thing
 * this app could do.
 */

const DRAFT_PREFIX = 'panels:draft:';
const OUTBOX_PREFIX = 'panels:outbox:';

export interface Draft extends DrawingSnapshot {
  savedAt: number;
}

export function saveDraft(panelId: string, snapshot: DrawingSnapshot): void {
  try {
    const draft: Draft = { ...snapshot, savedAt: Date.now() };
    localStorage.setItem(DRAFT_PREFIX + panelId, JSON.stringify(draft));
  } catch {
    // A full quota should never stop someone drawing; the outbox is what matters.
  }
}

export function loadDraft(panelId: string): Draft | null {
  try {
    const raw = localStorage.getItem(DRAFT_PREFIX + panelId);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Draft;
    if (!parsed || !Array.isArray(parsed.ops)) return null;
    return { ops: parsed.ops, redo: Array.isArray(parsed.redo) ? parsed.redo : [], savedAt: parsed.savedAt ?? 0 };
  } catch {
    return null;
  }
}

export function clearDraft(panelId: string): void {
  try {
    localStorage.removeItem(DRAFT_PREFIX + panelId);
  } catch {
    /* ignore */
  }
}

/* ---------------------------------------------------------------- outbox */

export interface OutboxEntry {
  panelId: string;
  ops: OpLog;
  idempotencyKey: string;
  queuedAt: number;
  attempts: number;
  lastError?: string;
  /**
   * Set when the server rejected the panel for good (too detailed, malformed…).
   * The entry and the draft are kept (guardrail #7), but nothing retries it
   * automatically; the player sees why and can try again by hand.
   */
  permanentError?: string;
}

export function queueSubmission(panelId: string, ops: OpLog): OutboxEntry {
  const existing = readOutbox(panelId);
  // Reuse the key across retries so the server can recognise a repeat.
  const entry: OutboxEntry = existing ?? {
    panelId,
    ops,
    idempotencyKey: crypto.randomUUID(),
    queuedAt: Date.now(),
    attempts: 0,
  };
  entry.ops = ops;
  writeOutbox(entry);
  return entry;
}

export function readOutbox(panelId: string): OutboxEntry | null {
  try {
    const raw = localStorage.getItem(OUTBOX_PREFIX + panelId);
    return raw ? (JSON.parse(raw) as OutboxEntry) : null;
  } catch {
    return null;
  }
}

function writeOutbox(entry: OutboxEntry): void {
  try {
    localStorage.setItem(OUTBOX_PREFIX + entry.panelId, JSON.stringify(entry));
  } catch {
    /* ignore */
  }
}

export function clearOutbox(panelId: string): void {
  try {
    localStorage.removeItem(OUTBOX_PREFIX + panelId);
  } catch {
    /* ignore */
  }
}

export function listOutbox(): OutboxEntry[] {
  const entries: OutboxEntry[] = [];
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key?.startsWith(OUTBOX_PREFIX)) continue;
      const raw = localStorage.getItem(key);
      if (!raw) continue;
      entries.push(JSON.parse(raw) as OutboxEntry);
    }
  } catch {
    /* ignore */
  }
  return entries;
}

export type SubmitState =
  | { phase: 'idle' }
  | { phase: 'sending'; attempt: number }
  | { phase: 'retrying'; attempt: number; nextAttemptIn: number; error: string }
  | { phase: 'confirmed' }
  | { phase: 'failed'; error: string };

const BACKOFF_MS = [1000, 2000, 4000, 8000, 15000, 30000];

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Push one queued panel to the server, retrying until it lands. The draft and the
 * outbox entry are cleared only once the server confirms — a duplicate submit
 * comes back as a success, so retrying is always safe.
 */
export async function flushSubmission(
  panelId: string,
  onState?: (state: SubmitState) => void,
  maxAttempts = 40,
): Promise<boolean> {
  const entry = readOutbox(panelId);
  if (!entry) return true;

  // A manual retry of a rejected panel gets one honest attempt.
  delete entry.permanentError;

  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    onState?.({ phase: 'sending', attempt: attempt + 1 });
    try {
      await submitPanel(entry.panelId, entry.ops, entry.idempotencyKey);
      clearOutbox(panelId);
      clearDraft(panelId);
      onState?.({ phase: 'confirmed' });
      return true;
    } catch (err) {
      const apiError = err instanceof ApiError ? err : null;
      const message = apiError?.message ?? 'Could not reach the server';
      // Anything that isn't an API answer (a bug, an aborted request) is worth retrying.
      const failure = apiError ? classifySubmitFailure(apiError.status, apiError.code) : 'retry';

      entry.attempts = attempt + 1;
      entry.lastError = message;

      if (failure === 'settled') {
        // Already submitted, or no longer this player's panel: nothing left to send.
        clearOutbox(panelId);
        clearDraft(panelId);
        onState?.({ phase: 'confirmed' });
        return true;
      }

      if (failure === 'permanent') {
        // The server said no, and asking again will not change that. Never loop:
        // stop, keep the drawing on this device, and say what happened.
        entry.permanentError = message;
        writeOutbox(entry);
        onState?.({ phase: 'failed', error: message });
        return false;
      }

      writeOutbox(entry);
      const delay = BACKOFF_MS[Math.min(attempt, BACKOFF_MS.length - 1)];
      onState?.({ phase: 'retrying', attempt: attempt + 1, nextAttemptIn: delay, error: message });
      await sleep(delay);
    }
  }

  onState?.({ phase: 'failed', error: 'Still trying to send your panel' });
  return false;
}

/**
 * Re-send anything stranded by a crash or a closed tab. Safe to call repeatedly.
 * Panels the server already rejected for good are left alone (see permanentError).
 */
export async function flushAllPending(): Promise<void> {
  for (const entry of listOutbox()) {
    if (entry.permanentError) continue;
    await flushSubmission(entry.panelId, undefined, 3);
  }
}
